import React from 'react';

import styled from '@emotion/styled';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FieldArray, useField } from 'formik';

import { COLORS } from '@clutter/clean';

import { Button, Col, Row } from '@shared/components/bootstrap';
import { FormikFieldFormGroup } from '@shared/components/fields/formik/formik_field_form_group';
import { FormikInputFormGroup } from '@shared/components/fields/formik/formik_input_form_group';

import { FieldFormGroupProps } from '@admin/components/fields';
import { FormikCouponSelectFormGroup } from '@admin/components/fields/formik/formik_coupon_select_form_group';
import {
  Availability__LaborAdjustmentsRangeInput,
  Coupon__FiltersInput,
  Coupon__Service,
  Coupon__Subject,
} from '@admin/schema';

const AdjustmentRange = styled.div`
  margin: 10px 0;
`;

const RemoveButton = styled.div`
  display: inline-block;
  cursor: pointer;
  color: ${COLORS.toucan};
`;

const AdjustmentRangeCol = styled(Col)`
  display: inline-block;
  float: none;
`;

const COUPONS_FILTERS: Coupon__FiltersInput = {
  active: true,
  subject: Coupon__Subject.Subscription,
  eligibleServices: [Coupon__Service.StorageOnboarding],
};

export const FormikAdjustmentRanges: React.FC<FieldFormGroupProps & { name: string }> = ({
  has,
  help,
  label,
  ...input
}) => {
  const [_, { value }] = useField(input.name);

  return (
    <FormikFieldFormGroup name={input.name} help={help} label={label} id={input.id}>
      <FieldArray
        name={input.name}
        render={({ push, remove }) => (
          <>
            {value.map((adjustment: Availability__LaborAdjustmentsRangeInput, idx: number) => (
              <AdjustmentRange key={`adjustment-${idx}`}>
                <Row>
                  <AdjustmentRangeCol md={2}>
                    <FormikInputFormGroup
                      id={`${input.name}_${idx}_low`}
                      name={`${input.name}[${idx}].lowThresholdPercentage`}
                      label="Low threshold"
                      type="number"
                      min={0}
                      max={100}
                    />
                  </AdjustmentRangeCol>
                  <AdjustmentRangeCol md={2}>
                    <FormikInputFormGroup
                      id={`${input.name}_${idx}_high`}
                      name={`${input.name}[${idx}].highThresholdPercentage`}
                      label="High threshold"
                      type="number"
                      min={0}
                      max={100}
                    />
                  </AdjustmentRangeCol>
                  <AdjustmentRangeCol md={2}>
                    <FormikInputFormGroup
                      id={`${input.name}_${idx}_adjustment`}
                      name={`${input.name}[${idx}].perMoverHourAdjustmentAmount`}
                      label="Per mover hour adjustment"
                      type="number"
                      min={-100}
                      max={100}
                    />
                  </AdjustmentRangeCol>
                  <AdjustmentRangeCol md={4}>
                    <FormikCouponSelectFormGroup
                      id={`${input.name}_${idx}_coupon`}
                      name={`${input.name}[${idx}].couponID`}
                      label="Coupon"
                      filters={COUPONS_FILTERS}
                    />
                  </AdjustmentRangeCol>
                  <AdjustmentRangeCol md={2}>
                    <RemoveButton onClick={() => remove(idx)}>
                      <FontAwesomeIcon icon={faTimes} />
                    </RemoveButton>
                  </AdjustmentRangeCol>
                </Row>
              </AdjustmentRange>
            ))}
            <div>
              <Button
                onClick={() =>
                  push({ lowThresholdPercentage: 0, highThresholdPercentage: 0, perMoverHourAdjustmentAmount: 0 })
                }
                kind="primary"
              >
                Add Range
              </Button>
            </div>
          </>
        )}
      />
    </FormikFieldFormGroup>
  );
};
